import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BasketContextProvider } from './context-basket';
import {
  SearchPage,
  CategoryPage,
  loader as categoryLoader,
} from './page-search';
import { LandingPage, loader as landingLoader } from './page-landing';
import { ProductPage, loader as productLoader } from './page-product';
import { ClusterPage, loader as clusterLoader } from './page-cluster';
import { BasketPage } from './page-basket';
import { FaqPage } from './page-faq';
import {
  MostReducedPage,
  loader as mostReducedLoader,
} from './page-most-reduced';
import { IntelligencePage } from './page-intelligence';
import { ErrorPage } from './page-error';
import { PrivacyPage } from './page-privacy';
import { onAnalyticsConsent } from './consent';
import posthog from 'posthog-js';

// Hidden dev spike (doc/barcode-scanning-plan.md "Step 0" in zuper-data),
// linked from nowhere. Lazy-loaded so the barcode-detector/zxing-wasm code
// it pulls in never lands in the main bundle -- the repo's first dynamic
// import(), Parcel code-splits it into its own chunk automatically.
const ScanTestPage = React.lazy(() => import('./page-scan-test'));

// Production barcode scanner (doc/barcode-scanning-plan.md Part 2, zuper-
// data). Lazy for the same reason as ScanTestPage above -- the
// barcode-detector/zxing-wasm chunk (and the wasm asset itself) must only
// ever be fetched once a user actually taps a scan button, not on every
// page load.
const ScanPage = React.lazy(() => import('./page-scan'));

let posthogInitialized = false;
let posthogOptedOut = false;

onAnalyticsConsent((granted) => {
  if (granted) {
    if (!posthogInitialized) {
      posthogInitialized = true;
      posthog.init('phc_uozP2AtXINhCsDbLhGYKf5zEvou1drz0ituFhnSptt2', {
        api_host: 'https://us.i.posthog.com',
        person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
      });
    } else if (posthogOptedOut) {
      posthog.opt_in_capturing();
    }
    posthogOptedOut = false;
  } else if (posthogInitialized) {
    posthog.opt_out_capturing();
    posthogOptedOut = true;
  }
});

const router = createBrowserRouter([
  {
    path: '/',
    element: <LandingPage />,
    loader: landingLoader,
    errorElement: <ErrorPage />,
  },
  {
    path: '/search',
    element: <SearchPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/category/*',
    element: <CategoryPage />,
    loader: categoryLoader,
    errorElement: <ErrorPage />,
  },
  {
    path: '/product/:id/:title',
    element: <ProductPage />,
    loader: productLoader,
  },
  {
    path: '/cluster/:id/:slug?',
    element: <ClusterPage />,
    loader: clusterLoader,
    errorElement: <ErrorPage />,
  },
  {
    path: '/basket',
    element: <BasketPage />,
  },
  {
    path: '/faq',
    element: <FaqPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/most-reduced',
    element: <MostReducedPage />,
    loader: mostReducedLoader,
  },
  {
    path: '/intelligence',
    element: <IntelligencePage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/scan-test',
    element: (
      <Suspense fallback={null}>
        <ScanTestPage />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/scan',
    element: (
      <Suspense fallback={null}>
        <ScanPage />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
]);

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <Helmet>
        <html lang="el" />
        <title>
          zuper — Σύγκριση τιμών σούπερ μάρκετ από ΑΒ, Σκλαβενίτη, Lidl και άλλα
        </title>
        <meta
          name="description"
          content="Πόσο κάνει ένα προϊόν στο σούπερ μάρκετ; Σύγκρινε εύκολα τιμές από 13 αλυσίδες (ΑΒ Βασιλόπουλος, Σκλαβενίτης, Lidl, Μασούτης, My Market, Κρητικός κ.ά.), δες πόσο κάνει το καθένα, τις μεγαλύτερες πτώσεις τιμών και υπολόγισε το καλάθι σου."
        />
        <meta
          name="keywords"
          content="πόσο κάνει, ποσο κανει, poso kanei, posokanei, σύγκριση τιμών σούπερ μάρκετ, τιμές σούπερ μάρκετ, σύγκριση τιμών"
        />
        <link rel="canonical" href="https://www.zuper-market.gr/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.zuper-market.gr/" />
        <meta
          property="og:title"
          content="zuper — Σύγκριση τιμών σούπερ μάρκετ"
        />
        <meta
          property="og:description"
          content="Σύγκρινε τιμές σε 13 αλυσίδες σούπερ μάρκετ και δες τις μεγαλύτερες πτώσεις τιμών."
        />
        <meta
          property="og:image"
          content="https://www.zuper-market.gr/social-preview.png"
        />
        <meta
          property="og:image:alt"
          content="zuper — σύγκριση τιμών σούπερ μάρκετ"
        />
        <meta property="og:locale" content="el_GR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="zuper — Σύγκριση τιμών σούπερ μάρκετ"
        />
        <meta
          name="twitter:description"
          content="Σύγκρινε τιμές σε 13 αλυσίδες σούπερ μάρκετ και δες τις μεγαλύτερες πτώσεις τιμών."
        />
      </Helmet>
      <BasketContextProvider>
        <RouterProvider router={router} />
      </BasketContextProvider>
    </HelmetProvider>
  </React.StrictMode>
);
